import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Divider,
  Stack,
  Chip,
  Paper,
  SelectChangeEvent,
} from '@mui/material';
import {
  Warning as WarningIcon,
  SwapHoriz as SwapIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  previewStructuralChangeDirect,
  createStructuralChangeDirect,
  getTeachersDirect,
  getSubjectsDirect,
  getRoomsDirect,
} from '@timetable/api';
import type { TimetableRecordWithRelations } from '@timetable/types';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../Toast';
import { LoadingSpinner } from '../LoadingSpinner';
import { queryKeys } from '../../lib/queryKeys';

interface StructuralChangeDialogProps {
  open: boolean;
  onClose: () => void;
  record: TimetableRecordWithRelations | null;
}

export function StructuralChangeDialog({
  open,
  onClose,
  record,
}: StructuralChangeDialogProps) {
  const { schoolContext, user } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [newTeacherId, setNewTeacherId] = useState('');
  const [newSubjectId, setNewSubjectId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Reset form when dialog opens/closes or record changes
  useEffect(() => {
    if (open && record) {
      setNewTeacherId(record.teacher_id);
      setNewSubjectId(record.subject_id);
      setRoomId(record.room?.id ?? '');
      setEffectiveDate(null);
      setShowPreview(false);
    }
  }, [open, record]);

  // Fetch teachers and subjects
  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '' }),
    enabled: open && !!schoolContext,
  });

  const { data: subjects, isLoading: loadingSubjects } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolContext || '' }),
    queryFn: () => getSubjectsDirect({ school_id: schoolContext || '' }),
    enabled: open && !!schoolContext,
  });

  const { data: rooms, isLoading: loadingRooms } = useQuery({
    queryKey: queryKeys.rooms.list({ school_id: schoolContext || '' }),
    queryFn: () => getRoomsDirect({ school_id: schoolContext || '' }),
    enabled: open && !!schoolContext,
  });
  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: () => {
      if (!record || !effectiveDate) {
        throw new Error('Missing required data');
      }
      const effectiveDateStr = effectiveDate.toISOString().split('T')[0];
      return previewStructuralChangeDirect(
        record.id,
        newTeacherId,
        newSubjectId,
        effectiveDateStr
      );
    },
    onSuccess: () => {
      setShowPreview(true);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to preview changes');
    },
  });

  // Create structural change mutation
  const createMutation = useMutation({
    mutationFn: () => {
      if (!record || !effectiveDate) {
        throw new Error('Missing required data');
      }
      const effectiveDateStr = effectiveDate.toISOString().split('T')[0];
      return createStructuralChangeDirect({
        old_record_id: record.id,
        new_teacher_id: newTeacherId,
        new_subject_id: newSubjectId,
        effective_date: effectiveDateStr,
        created_by: user?.id,
        room_id: roomId || null,
      });
    },
    onSuccess: () => {
      toast.success('Structural change created successfully');
      // Invalidate timetable queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      handleClose();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create structural change');
    },
  });

  const handleClose = () => {
    setNewTeacherId('');
    setNewSubjectId('');
    setRoomId('');
    setEffectiveDate(null);
    setShowPreview(false);
    onClose();
  };

  const handleTeacherChange = (event: SelectChangeEvent<string>) => {
    setNewTeacherId(event.target.value);
    setShowPreview(false);
  };

  const handleSubjectChange = (event: SelectChangeEvent<string>) => {
    setNewSubjectId(event.target.value);
    setShowPreview(false);
  };

  const handleRoomChange = (event: SelectChangeEvent<string>) => {
    setRoomId(event.target.value);
    setShowPreview(false);
  };

  const handleDateChange = (date: Date | null) => {
    setEffectiveDate(date);
    setShowPreview(false);
  };

  const handlePreview = () => {
    previewMutation.mutate();
  };

  const handleConfirm = () => {
    createMutation.mutate();
  };

  const isFormValid = () => {
    if (!record || !effectiveDate || !newTeacherId || !newSubjectId) {
      return false;
    }

    // Check if effective date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const effective = new Date(effectiveDate);
    effective.setHours(0, 0, 0, 0);

    if (effective <= today) {
      return false;
    }

    // Check if anything actually changed
    const hasChanges =
      newTeacherId !== record.teacher_id ||
      newSubjectId !== record.subject_id ||
      (roomId || null) !== (record.room?.id ?? null);

    return hasChanges;
  };

  const getDayBeforeEffectiveDate = () => {
    if (!effectiveDate) return null;
    const dayBefore = new Date(effectiveDate);
    dayBefore.setDate(dayBefore.getDate() - 1);
    return dayBefore.toLocaleDateString();
  };

  if (!record) return null;

  const isLoading = loadingTeachers || loadingSubjects || loadingRooms;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="structural-change-dialog-title"
      >
        <DialogTitle id="structural-change-dialog-title">
          Make Structural Change
        </DialogTitle>

        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Warning Alert */}
              <Alert severity="warning" icon={<WarningIcon />}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Permanent Change Warning
                </Typography>
                <Typography variant="body2">
                  This will permanently modify the base timetable starting from the
                  effective date. This is NOT a temporary substitution. Use the absence
                  recording feature for temporary teacher replacements.
                </Typography>
              </Alert>

              {/* Current Record Info */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Current Assignment
                </Typography>
                <Stack spacing={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      Class:
                    </Typography>
                    <Typography variant="body2">
                      {record.class_section.class.class_name}-
                      {record.class_section.section}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      Day:
                    </Typography>
                    <Typography variant="body2">{record.day_of_week}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      Period:
                    </Typography>
                    <Typography variant="body2">
                      Period {record.period.period_number} ({record.period.time_start} -{' '}
                      {record.period.time_end})
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      Teacher:
                    </Typography>
                    <Typography variant="body2">{record.teacher.name}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      Subject:
                    </Typography>
                    <Typography variant="body2">{record.subject.subject_name}</Typography>
                  </Box>
                </Stack>
              </Paper>

              <Divider>
                <Chip label="Change To" icon={<SwapIcon />} />
              </Divider>

              {/* New Teacher Selection */}
              <FormControl fullWidth>
                <InputLabel id="new-teacher-label">New Teacher</InputLabel>
                <Select
                  labelId="new-teacher-label"
                  id="new-teacher-select"
                  value={newTeacherId}
                  label="New Teacher"
                  onChange={handleTeacherChange}
                >
                  {teachers?.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* New Subject Selection */}
              <FormControl fullWidth>
                <InputLabel id="new-subject-label">New Subject</InputLabel>
                <Select
                  labelId="new-subject-label"
                  id="new-subject-select"
                  value={newSubjectId}
                  label="New Subject"
                  onChange={handleSubjectChange}
                >
                  {subjects?.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.subject_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Room Selection (optional) */}
              <FormControl fullWidth>
                <InputLabel id="room-label">Room (optional)</InputLabel>
                <Select
                  labelId="room-label"
                  id="room-select"
                  value={roomId}
                  label="Room (optional)"
                  onChange={handleRoomChange}
                >
                  <MenuItem value="">None / No room</MenuItem>
                  {rooms?.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Effective Date Picker */}
              <DatePicker
                label="Effective Date"
                value={effectiveDate}
                onChange={handleDateChange}
                minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Select a future date when this change takes effect',
                    InputProps: {
                      startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                    },
                  },
                }}
              />

              {/* Preview Section */}
              {showPreview && previewMutation.data && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Preview of Changes
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Old Record (will be closed):
                      </Typography>
                      <Stack spacing={0.5} sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          • Teacher: {previewMutation.data.oldRecord.teacher.name}
                        </Typography>
                        <Typography variant="body2">
                          • Subject:{' '}
                          {previewMutation.data.oldRecord.subject.subject_name}
                        </Typography>
                        <Typography variant="body2">
                          • Active until: {getDayBeforeEffectiveDate()}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        New Record (will be created):
                      </Typography>
                      <Stack spacing={0.5} sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          • Teacher:{' '}
                          {previewMutation.data.newRecordPreview.teacher?.name}
                        </Typography>
                        <Typography variant="body2">
                          • Subject:{' '}
                          {previewMutation.data.newRecordPreview.subject?.subject_name}
                        </Typography>
                        <Typography variant="body2">
                          • Active from:{' '}
                          {effectiveDate?.toLocaleDateString()}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          {!showPreview ? (
            <Button
              onClick={handlePreview}
              variant="outlined"
              disabled={!isFormValid() || previewMutation.isPending}
            >
              {previewMutation.isPending ? 'Loading...' : 'Preview Changes'}
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="warning"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Confirm Structural Change'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
